import { ArrowLeft, Home, Building, Store, TrendingUp, Search, Calendar, ArrowUpRight, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useEffect, useState } from "react";
import apiClient from "../common/apiClient";
import Cookies from "js-cookie";
import decryptResponse from "../common/decrypt";
import { useUserProfile, DEFAULT_PROPERTY_CITIES, CITY_LABELS_GU } from "../contexts/UserProfileContext";
import { FEATURES } from "../constants/feature.constants";
import { openPremiumRequiredPopup } from "../components/PremiumRequiredPopup";
import { showSessionExpiredPopup } from "../components/SessionExpiredPopup";

const flowData = [
  { day: "Mon", added: 12, sold: 5 },
  { day: "Tue", added: 18, sold: 8 },
  { day: "Wed", added: 9, sold: 4 },
  { day: "Thu", added: 22, sold: 11 },
  { day: "Fri", added: 15, sold: 7 },
  { day: "Sat", added: 28, sold: 14 },
  { day: "Sun", added: 10, sold: 3 },
];

const subCategories = [
  { en: "Residential Rent", gu: "રહેણાંક ભાડે", value: "#1,396", icon: Building, bg: "bg-[hsl(30,100%,90%)]", color: "text-[hsl(30,100%,45%)]" },
  { en: "Residential Sell", gu: "રહેણાંક વેચાણ", value: "#12,170", icon: TrendingUp, bg: "bg-[hsl(90,50%,88%)]", color: "text-[hsl(90,50%,35%)]" },
  { en: "Commercial Rent", gu: "કોમર્શિયલ ભાડે", value: "#3,014", icon: Store, bg: "bg-[hsl(170,60%,88%)]", color: "text-[hsl(170,60%,35%)]" },
  { en: "Commercial Sell", gu: "કોમર્શિયલ વેચાણ", value: "#4,675", icon: Store, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
];

const stats = [
  { en: "Added Today", gu: "આજે ઉમેર્યા", value: "18", icon: Calendar, bg: "bg-[hsl(142,60%,88%)]", color: "text-[hsl(142,60%,35%)]" },
  { en: "This Week", gu: "આ અઠવાડિયે", value: "42", icon: ArrowUpRight, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
  { en: "This Month", gu: "આ મહિને", value: "189", icon: BarChart3, bg: "bg-[hsl(38,90%,88%)]", color: "text-[hsl(38,90%,40%)]" },
];

const OwnerProperties = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [propertyCounts, setPropertyCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const { userProfile, hasFeature, hasCityAccess, propertyAccessCities } = useUserProfile();
  const [selectedCity, setSelectedCity] = useState(
    localStorage.getItem("selectedCity") || "Ahmedabad West"
  );

  // Driven by PropertyAccessCity: only the cities the user actually has access to
  // are rendered. Falls back to the default list until the profile resolves.
  const cityNames = propertyAccessCities.length ? propertyAccessCities : DEFAULT_PROPERTY_CITIES;
  const cities = cityNames.map((name) => ({
    value: name,
    label: t(name, CITY_LABELS_GU[name] || name),
  }));

  const checkSession = async () => {
    try {
      const token = Cookies.get("token");
      if (!token) return;

      const sessionResponse = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/v2/checkSession",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      let finalData = sessionResponse;
      if (sessionResponse?.error?.encrypted) {
        const decrypted = decryptResponse(sessionResponse.error.encrypted);
        if (decrypted) finalData = decrypted;
      }

      if (
        finalData.success === false ||
        finalData.status === "error" ||
        finalData.status === 401
      ) {
        if (finalData.action === "logout" || finalData.status === 401) {
          handleLogout(finalData.message || "Session expired");
        } else if (finalData.error?.action === "logout") {
          handleLogout(finalData.error.message || "Session expired");
        }
      }
    } catch (error) {
      console.error("Error checking session:", error);
    }
  };

  const handleLogout = (message) => {
    Cookies.remove("token");
    Cookies.remove("userId");
    Cookies.remove("isPremium");
    Cookies.remove("deviceId");
    localStorage.clear();
    navigate("/login");
    showSessionExpiredPopup(message);
  };

  const handlePropertyClick = (type, status, listedBy) => {
    const isLoggedIn = Cookies.get("userId");
    const isPremium = Cookies.get("isPremium");
    window.scrollTo(0, 0);

    if (isPremium === "1") {
      navigate("/residential-rental", {
        state: { type, status, listedBy, scrollToTop: true },
      });
    } else if (isLoggedIn && isPremium === "0") {
      openPremiumRequiredPopup();
    } else {
      navigate("/login");
    }
  };

  const fetchCounts = async (city = selectedCity) => {
    setLoading(true);
    try {
      const res = await apiClient(
        "get",
        "/cjidnvij/ceksfbuebijn/user/counts/fjkbfhwb/fkjbwdiwhbdjwkfjwbj",
        { city, status: "Probroker" }
      );
      if (res.success) {
        setPropertyCounts(res.data);
      }
    } catch (error) {
      console.error("Error fetching owner property counts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCityChange = (city) => {
    setSelectedCity(city);
    localStorage.setItem("selectedCity", city); // persist so other pages use the same city
    fetchCounts(city);
  };

  useEffect(() => {
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-switch to the first accessible city if the current one isn't in PropertyAccessCity.
  useEffect(() => {
    if (propertyAccessCities.length === 0) return; // no restriction / not loaded yet
    if (hasCityAccess(selectedCity)) return; // current selection already allowed
    const firstAllowed = cities.find((c) => hasCityAccess(c.value));
    if (firstAllowed && firstAllowed.value !== selectedCity) {
      handleCityChange(firstAllowed.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAccessCities.join("|")]);

  const ownerPropertiesData = [
    { en: "Total Property", gu: "કુલ પ્રોપર્ટી", val: propertyCounts.totalActiveProperties || 0, img: "/image/5.png", icon: Home, bg: "bg-[hsl(262,83%,92%)]", color: "text-primary" },
    { en: "Residential Rent", gu: "રહેણાંક ભાડે", val: propertyCounts.activeResidentialRental || 0, img: "/image/residental-rent.png", icon: Building, bg: "bg-[hsl(30,100%,90%)]", color: "text-[hsl(30,100%,45%)]" },
    { en: "Residential Sell", gu: "રહેણાંક વેચાણ", val: propertyCounts.activeResidentialSell || 0, img: "/image/2.png", icon: TrendingUp, bg: "bg-[hsl(90,50%,88%)]", color: "text-[hsl(90,50%,35%)]" },
    { en: "Commercial Rent", gu: "કોમર્શિયલ ભાડે", val: propertyCounts.activeCommercialRent || 0, img: "/image/3.png", icon: Store, bg: "bg-[hsl(170,60%,88%)]", color: "text-[hsl(170,60%,35%)]" },
    { en: "Commercial Sell", gu: "કોમર્શિયલ વેચાણ", val: propertyCounts.activeCommercialSell || 0, img: "/image/4.png", icon: Store, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
  ];

  const statsData = [
    { en: "Added Today", gu: "આજે ઉમેર્યા", value: propertyCounts.addedToday || 0, icon: Calendar, bg: "bg-[hsl(142,60%,88%)]", color: "text-[hsl(142,60%,35%)]" },
    { en: "This Week", gu: "આ અઠવાડિયે", value: propertyCounts.thisWeek || 0, icon: ArrowUpRight, bg: "bg-[hsl(210,80%,90%)]", color: "text-[hsl(210,80%,45%)]" },
    { en: "This Month", gu: "આ મહિને", value: propertyCounts.thisMonth || 0, icon: BarChart3, bg: "bg-[hsl(38,90%,88%)]", color: "text-[hsl(38,90%,40%)]" },
  ];

  const chartData = propertyCounts.propertyFlow?.length > 0
    ? propertyCounts.propertyFlow.map((item) => ({
      day: item.day,
      added: item.ocount || 0,
      sold: (item.soldOut || 0) + (item.rentOut || 0)
    }))
    : flowData;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        {/* On mobile the search button sits to the right of the title and the
            cities drop to their own scrollable row; from md up all three share
            one row again (title left, cities + search right). */}
        <div className="flex items-center justify-between gap-x-3 gap-y-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="md:hidden p-1 -ml-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl md:text-2xl font-bold truncate">{t("Owner Properties", "માલિક પ્રોપર્ટી")}</h1>
          </div>
          <Button
            size="sm"
            className="order-2 md:order-3 ml-auto md:ml-0 shrink-0 gradient-primary border-0 text-primary-foreground gap-1.5 shadow-md"
            onClick={() => navigate("/filter")}
          >
            <Search size={18} /><span className="md:hidden">Search</span>
            <span className="hidden md:inline">{t("Search Property", "પ્રોપર્ટી શોધો")}</span>
          </Button>
          <div className="order-3 md:order-2 w-full md:w-auto md:ml-auto min-w-0 max-w-full">
            <div className="flex items-center gap-1.5 flex-nowrap min-w-0 max-w-full overflow-x-auto py-1 -my-1">
              {cities.map((city) => {
                const cityAllowed = hasCityAccess(city.value);
                return (
                  <Button
                    key={city.value}
                    size="sm"
                    variant={selectedCity === city.value ? "default" : "outline"}
                    disabled={!cityAllowed}
                    title={!cityAllowed ? t("No access to this city", "આ શહેરની ઍક્સેસ નથી") : undefined}
                    className={`h-8 px-2 text-[11px] lg:h-9 lg:px-3 lg:text-sm whitespace-nowrap shrink-0 ${
                      selectedCity === city.value
                        ? "gradient-primary border-0 text-primary-foreground shadow-md"
                        : ""
                    } ${!cityAllowed ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => cityAllowed && handleCityChange(city.value)}
                  >
                    {city.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Property Overview */}

      <div className={`space-y-3 ${!hasFeature(FEATURES.OWNER_PROPERTY) ? "opacity-75" : ""}`}>
        <div className="flex items-center justify-center gap-2">
          {/* <h2 className="text-lg font-bold text-center underline decoration-primary decoration-2 underline-offset-4">
            {t("Property Overview", "પ્રોપર્ટી ઓવરવ્યૂ")}
          </h2> */}
          {!hasFeature(FEATURES.OWNER_PROPERTY) && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-bold uppercase tracking-wider flex items-center gap-1">
              {t("Locked", "લોક")}
            </span>
          )}
        </div>


        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 lg:gap-3 xl:gap-4">
          {ownerPropertiesData.map((item) => {
            const isTotal = item.en === "Total Property";
            const isLocked = !hasFeature(FEATURES.OWNER_PROPERTY);
            return (
              <div
                key={item.en}
                className={`glass-card rounded-xl p-3 md:p-5 flex transition-all duration-200 border-2 border-primary/20 
                  ${isLocked 
                    ? "cursor-not-allowed grayscale-[0.5]" 
                    : "cursor-pointer hover:border-primary/40"} 
                  ${isTotal
                    ? "col-span-2 lg:col-span-1 flex-row items-center justify-center lg:justify-start gap-4"
                    : "items-center gap-3 md:gap-4"}`}
                onClick={() => !isLocked && handlePropertyClick(isTotal ? "All" : item.en, "active", "Owner")}
              >
                <div className={`${isTotal ? "w-12 lg:w-12 h-12 lg:h-12" : "w-9 lg:w-10 xl:w-12 h-9 lg:h-10 xl:h-12"} rounded-full ${item.img ? "" : item.bg} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                  {item.img ? (
                    <img src={item.img} alt={item.en} className="w-full h-full object-contain" />
                  ) : (
                    <item.icon size={isTotal ? 26 : 20} className="lg:w-6 lg:h-6 xl:w-8 xl:h-8" />
                  )}
                </div>
                <div>
                  {loading ? (
                    <div className={`skeleton-shimmer rounded-md ${isTotal ? "h-6 w-16" : "h-5 w-12"} mb-1`} />
                  ) : (
                    <p className={`${isTotal ? "text-xl lg:text-lg xl:text-xl" : "text-base lg:text-lg xl:text-xl"} font-bold`}>#{item.val?.toLocaleString() || "0"}</p>
                  )}
                  <p className={`${isTotal ? "text-xs lg:text-[10px] xl:text-xs" : "text-[10px] lg:text-xs"} text-muted-foreground`}>{t(item.en, item.gu)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Summary */}
      <div className="space-y-3">
        <h2 className="text-base lg:text-lg font-bold text-center underline decoration-primary decoration-2 underline-offset-4">
          {t("Activity Summary", "પ્રવૃત્તિ સારાંશ")}
        </h2>
        <div className="grid grid-cols-3 gap-2 lg:gap-4">
          {statsData.map((s) => (
            <div key={s.en} className="glass-card rounded-xl p-3 lg:p-5 flex items-center gap-2 lg:gap-4 border-2 border-primary/20">
              <div className={`w-9 lg:w-12 h-9 lg:h-12 rounded-full ${s.bg} flex items-center justify-center flex-shrink-0`}>
                <s.icon size={18} className="lg:w-6 lg:h-6" />
              </div>
              <div>
                {loading ? (
                  <div className="skeleton-shimmer rounded-md h-5 w-10 mb-1" />
                ) : (
                  <p className="text-base lg:text-lg xl:text-xl font-bold">{s.value?.toLocaleString() || "0"}</p>
                )}
                <p className="text-[10px] lg:text-xs text-muted-foreground">{t(s.en, s.gu)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Property Flow Chart */}
      <div className="glass-card rounded-xl p-3 lg:p-5">
        <h3 className="font-semibold mb-3 lg:mb-4 text-sm lg:text-base">{t("Properties Flow — Last 7 Days", "પ્રોપર્ટી ફ્લો — છેલ્લા ૭ દિવસ")}</h3>
        <div className="h-52 lg:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barSize={40}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip />
              <Bar dataKey="added" name={t("Added", "ઉમેર્યા")} fill="hsl(262 83% 58%)" radius={[6, 6, 0, 0]} />
              {/* <Bar dataKey="sold" name={t("Sold/Rented", "વેચ્યા/ભાડે")} fill="hsl(142 71% 45%)" radius={[6, 6, 0, 0]} /> */}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default OwnerProperties;
